import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { Check, Copy, Download, ExternalLink, FileText, Globe } from 'lucide-react';
import { cn } from '../../utils';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

type PreviewKind = 'image' | 'pdf' | 'doc';

interface PreviewLink {
  url: string;
  filename: string;
  kind: PreviewKind;
}

interface LinkCard {
  url: string;
  label: string;
  host: string;
  path: string;
  favicon: string | null;
}

const MARKDOWN_IMAGE_REGEX = /!\[[^\]]*\]\(([^)\s]+)(?:\s+"[^"]*")?\)/gi;
const MARKDOWN_LINK_REGEX = /\[([^\]]+)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/gi;
const RAW_PREVIEW_URL_REGEX = /(?:https?:\/\/[^\s<>()]+|\/[^\s<>()]+?\.(?:png|jpe?g|gif|webp|pdf|docx?)(?:\?[^\s<>()]*)?)/gi;
const RAW_URL_REGEX = /https?:\/\/[^\s<>()]+/gi;

function getLanguageLabel(className?: string) {
  const match = /language-([\w-]+)/i.exec(className || '');
  return match?.[1] || 'code';
}

function shouldRenderBlockCodeInline(rawCode: string) {
  const trimmed = rawCode.trim();
  return Boolean(trimmed) && trimmed.length < 80 && !/\s/.test(trimmed);
}

function extractTextContent(children: React.ReactNode): string {
  if (typeof children === 'string' || typeof children === 'number') {
    return String(children);
  }

  if (Array.isArray(children)) {
    return children.map((child) => extractTextContent(child)).join('');
  }

  if (React.isValidElement(children)) {
    return extractTextContent(children.props.children);
  }

  return '';
}

function normalizePreviewUrl(url: string) {
  return url.replace(/[),.;!?]+$/, '');
}

function buildAbsoluteUrl(url: string) {
  try {
    return new URL(url, typeof window !== 'undefined' ? window.location.origin : 'https://ally.local');
  } catch {
    return null;
  }
}

function resolveUrlPath(url: string) {
  return buildAbsoluteUrl(url)?.pathname.toLowerCase() || url.toLowerCase();
}

function getPreviewKind(url: string): PreviewKind | null {
  const path = resolveUrlPath(url);
  if (/\.(png|jpe?g|gif|webp)$/i.test(path)) return 'image';
  if (/\.pdf$/i.test(path)) return 'pdf';
  if (/\.docx?$/i.test(path)) return 'doc';
  return null;
}

function getPreviewFilename(url: string) {
  const resolved = buildAbsoluteUrl(url);
  if (resolved) {
    const lastSegment = resolved.pathname.split('/').filter(Boolean).pop();
    return lastSegment || 'Attachment';
  }

  const fallback = url.split('/').pop()?.split('?')[0];
  return fallback || 'Attachment';
}

function getLinkCard(url: string, label?: string): LinkCard | null {
  const resolved = buildAbsoluteUrl(url);
  if (!resolved || !/^https?:$/i.test(resolved.protocol)) {
    return null;
  }

  const host = resolved.hostname.replace(/^www\./i, '');
  const path = `${resolved.pathname || '/'}${resolved.search || ''}`;

  return {
    url: resolved.toString(),
    label: (label || host).trim(),
    host,
    path: path.length > 72 ? `${path.slice(0, 69)}...` : path,
    favicon: `https://www.google.com/s2/favicons?domain=${encodeURIComponent(resolved.hostname)}&sz=64`,
  };
}

function extractPreviewLinks(content: string): PreviewLink[] {
  const excludedUrls = new Set<string>();
  const found = new Map<string, PreviewLink>();

  for (const match of content.matchAll(MARKDOWN_IMAGE_REGEX)) {
    const url = normalizePreviewUrl(match[1] || '');
    if (!url) continue;
    excludedUrls.add(url);
  }

  const addPreview = (rawUrl: string) => {
    const url = normalizePreviewUrl(rawUrl);
    if (!url || found.has(url) || excludedUrls.has(url)) {
      return;
    }

    const kind = getPreviewKind(url);
    if (!kind) {
      return;
    }

    found.set(url, {
      url,
      kind,
      filename: getPreviewFilename(url),
    });
  };

  for (const match of content.matchAll(MARKDOWN_LINK_REGEX)) {
    addPreview(match[2] || '');
  }

  for (const match of content.match(RAW_PREVIEW_URL_REGEX) || []) {
    addPreview(match);
  }

  return Array.from(found.values());
}

function extractLinkCards(content: string, previews: PreviewLink[]): LinkCard[] {
  const previewUrls = new Set(previews.map((preview) => normalizePreviewUrl(preview.url)));
  const found = new Map<string, LinkCard>();

  const addCard = (rawUrl: string, label?: string) => {
    const url = normalizePreviewUrl(rawUrl);
    if (!url || previewUrls.has(url) || found.has(url)) {
      return;
    }

    const previewKind = getPreviewKind(url);
    if (previewKind) {
      return;
    }

    const card = getLinkCard(url, label);
    if (!card) {
      return;
    }

    found.set(url, card);
  };

  for (const match of content.matchAll(MARKDOWN_LINK_REGEX)) {
    addCard(match[2] || '', match[1] || '');
  }

  for (const match of content.match(RAW_URL_REGEX) || []) {
    addCard(match);
  }

  return Array.from(found.values());
}

function openPreview(url: string) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

function downloadPreview(url: string, filename: string) {
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function MarkdownCodeBlock({ className, children }: { className?: string; children: React.ReactNode }) {
  // D7-06 (FE-6.5): shared copy hook replaces the local copied/timeout state.
  const { copied, copy } = useCopyToClipboard();
  const rawCode = React.useMemo(() => extractTextContent(children).replace(/\n$/, ''), [children]);
  const language = getLanguageLabel(className);
  const shouldRenderInline = React.useMemo(() => shouldRenderBlockCodeInline(rawCode), [rawCode]);

  const handleCopy = React.useCallback(async () => {
    await copy(rawCode);
  }, [copy, rawCode]);

  if (shouldRenderInline) {
    return <code className="rounded bg-slate-800 px-1.5 py-0.5 font-mono text-sm text-slate-100">{rawCode.trim()}</code>;
  }

  return (
    <div className="my-3 overflow-hidden rounded-2xl border border-slate-700/80 bg-slate-950/95 shadow-sm shadow-slate-950/30">
      <div className="flex items-center justify-between border-b border-slate-700/80 bg-slate-900/80 px-3 py-2">
        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">{language}</span>
        <button
          type="button"
          onClick={() => void handleCopy()}
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-slate-300 transition-colors hover:bg-slate-800 hover:text-white"
          title={copied ? 'Copied!' : 'Copy code'}
          aria-label={copied ? 'Code copied' : 'Copy code'}
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <pre className="m-0 overflow-x-auto p-4">
        <code className={className}>{children}</code>
      </pre>
    </div>
  );
}

function MarkdownInlineImage({ src, alt }: { src?: string; alt?: string }) {
  if (!src) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={() => openPreview(src)}
      className="my-3 block w-full overflow-hidden rounded-2xl border border-slate-700/80 bg-slate-950 text-left transition hover:border-slate-600"
      aria-label={`Open image${alt ? ` ${alt}` : ''} in a new tab`}
      title="Open full size"
    >
      <img
        src={src}
        alt={alt || 'Shared image'}
        className="max-h-[28rem] w-full object-contain"
        loading="lazy"
      />
      <div className="border-t border-slate-800 px-3 py-2 text-xs text-slate-300">Click to open full size</div>
    </button>
  );
}

function PreviewGallery({ previews }: { previews: PreviewLink[] }) {
  if (previews.length === 0) {
    return null;
  }

  return (
    <div className="mt-3 space-y-3">
      {previews.map((preview) => {
        if (preview.kind === 'image') {
          return (
            <button
              key={preview.url}
              type="button"
              onClick={() => openPreview(preview.url)}
              className="block w-full overflow-hidden rounded-2xl border border-slate-700/80 bg-slate-950 text-left transition hover:border-slate-600"
              aria-label={`Open image ${preview.filename}`}
              title="Open full size"
            >
              <img
                src={preview.url}
                alt={preview.filename}
                className="max-h-[28rem] w-full object-contain"
                loading="lazy"
              />
              <div className="flex items-center justify-between border-t border-slate-800 px-3 py-2 text-xs text-slate-300">
                <span className="truncate">{preview.filename}</span>
                <span className="text-slate-400">Open full size</span>
              </div>
            </button>
          );
        }

        const isPdf = preview.kind === 'pdf';

        return (
          <div
            key={preview.url}
            className="flex items-center gap-3 rounded-2xl border border-slate-700/80 bg-slate-950 px-4 py-3"
          >
            <div className={cn('flex h-10 w-10 items-center justify-center rounded-xl', isPdf ? 'bg-red-500/10 text-red-300' : 'bg-blue-500/10 text-blue-300')}>
              <FileText className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-slate-100">{preview.filename}</div>
              <div className="text-xs text-slate-400">{isPdf ? 'PDF preview available' : 'Document attachment'}</div>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => openPreview(preview.url)}
                className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs text-slate-300 transition hover:bg-slate-800 hover:text-white"
                aria-label={`Open ${preview.filename}`}
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Open
              </button>
              <button
                type="button"
                onClick={() => downloadPreview(preview.url, preview.filename)}
                className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs text-slate-300 transition hover:bg-slate-800 hover:text-white"
                aria-label={`Download ${preview.filename}`}
              >
                <Download className="h-3.5 w-3.5" />
                Download
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function LinkPreviewGallery({ links }: { links: LinkCard[] }) {
  if (links.length === 0) {
    return null;
  }

  return (
    <div className="mt-3 space-y-2">
      {links.map((link) => (
        <a
          key={link.url}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-center gap-3 rounded-2xl border border-slate-700/80 bg-slate-950/80 px-4 py-3 transition hover:border-blue-500/50 hover:bg-slate-950"
        >
          <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl bg-slate-900 ring-1 ring-slate-700/80">
            {link.favicon ? (
              <img src={link.favicon} alt="" className="h-5 w-5" loading="lazy" onError={(event) => { event.currentTarget.style.display = 'none'; }} />
            ) : null}
            <Globe className="h-4 w-4 text-slate-400" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-slate-100 group-hover:text-white">{link.label}</div>
            <div className="truncate text-xs text-slate-400">{link.host}{link.path && link.path !== '/' ? link.path : ''}</div>
          </div>
          <ExternalLink className="h-4 w-4 shrink-0 text-slate-500 transition group-hover:text-blue-300" />
        </a>
      ))}
    </div>
  );
}

export function MarkdownRenderer({ content, className }: MarkdownRendererProps) {
  const previews = React.useMemo(() => extractPreviewLinks(content), [content]);
  const linkCards = React.useMemo(() => extractLinkCards(content, previews), [content, previews]);

  return (
    <div className={cn('ally-markdown', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight]}
        components={{
          pre({ children }) {
            return <>{children}</>;
          },
          p({ children }) {
            return <p className="my-2 leading-7 text-slate-100 first:mt-0 last:mb-0">{children}</p>;
          },
          code({ inline, className: codeClassName, children, ...props }: any) {
            const rawCode = extractTextContent(children).replace(/\n$/, '');

            if (inline || shouldRenderBlockCodeInline(rawCode)) {
              return (
                <code className="rounded bg-slate-800 px-1.5 py-0.5 font-mono text-sm text-slate-100" {...props}>
                  {inline ? children : rawCode.trim()}
                </code>
              );
            }

            return <MarkdownCodeBlock className={codeClassName} {...props}>{children}</MarkdownCodeBlock>;
          },
          table({ children }) {
            return (
              <div className="my-4 overflow-hidden rounded-2xl border border-slate-700/80 bg-slate-950/60 shadow-sm shadow-slate-950/30">
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse text-sm">{children}</table>
                </div>
              </div>
            );
          },
          thead({ children }) {
            return <thead className="bg-slate-900/90 text-slate-100">{children}</thead>;
          },
          tbody({ children }) {
            return <tbody className="divide-y divide-slate-800/80">{children}</tbody>;
          },
          tr({ children }) {
            return <tr className="odd:bg-slate-950/30 even:bg-slate-900/30">{children}</tr>;
          },
          th({ children }) {
            return <th className="border-b border-slate-700/80 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-300">{children}</th>;
          },
          td({ children }) {
            return <td className="px-3 py-2 align-top text-slate-100">{children}</td>;
          },
          ol({ children }) {
            return (
              <ol className="my-4 list-decimal space-y-3 rounded-2xl border border-blue-500/20 bg-blue-500/5 px-6 py-4 marker:font-semibold marker:text-blue-300">
                {children}
              </ol>
            );
          },
          ul({ children }) {
            return <ul className="my-3 list-disc space-y-2 pl-5 marker:text-slate-400">{children}</ul>;
          },
          li({ children }) {
            return <li className="pl-1 leading-7 text-slate-100">{children}</li>;
          },
          blockquote({ children }) {
            return <blockquote className="my-4 rounded-r-2xl border-l-4 border-blue-400/70 bg-slate-950/50 px-4 py-3 text-slate-200">{children}</blockquote>;
          },
          a({ href, children, ...props }) {
            return (
              <a href={href} target="_blank" rel="noopener noreferrer" className="font-medium text-blue-300 underline decoration-blue-400/40 underline-offset-4 transition hover:text-blue-200" {...props}>
                {children}
              </a>
            );
          },
          img({ src, alt }) {
            return <MarkdownInlineImage src={src} alt={alt} />;
          },
          h1({ children }) {
            return <h1 className="mt-1 text-xl font-semibold text-slate-50">{children}</h1>;
          },
          h2({ children }) {
            return <h2 className="mt-1 text-lg font-semibold text-slate-50">{children}</h2>;
          },
          h3({ children }) {
            return <h3 className="mt-1 text-base font-semibold text-slate-100">{children}</h3>;
          },
          h4({ children }) {
            return <h4 className="mt-1 text-sm font-semibold uppercase tracking-wide text-slate-200">{children}</h4>;
          },
          h5({ children }) {
            return <div className="mt-1 text-sm font-semibold text-slate-100">{children}</div>;
          },
          h6({ children }) {
            return <div className="mt-1 text-sm font-semibold text-slate-100">{children}</div>;
          },
          hr() {
            return <hr className="my-4 border-slate-800" />;
          },
        }}
      >
        {content}
      </ReactMarkdown>
      <PreviewGallery previews={previews} />
      <LinkPreviewGallery links={linkCards} />
    </div>
  );
}

export default MarkdownRenderer;
